import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft, faAngleRight, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import PageHeadingSection from '../../../../common/pageheading/PageHeadingSection';
import apiServices from '../../../../../services/apiService';
import CustomSelect from '../../../../common/formfields/CustomSelect';
import MenuHierarchySelect from '../../../../common/formfields/MenuHierarchySelect';
import { primaryColor, secondaryColor } from '../../../../../utils/comon';
import storageService from '../../../../../utils/storageService';
import Breadcrumbs from '../../../../common/BreadCrum/BreadCrum';

const RoleForm = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isLoading, setIsLoading] = useState(false);
    const [isFetching, setIsFetching] = useState(false);

    const [menus, setMenus] = useState([]);

    const [formData, setFormData] = useState({
        roleName: '',
        menuListId: [],
        isActive: 'Active'
    });

    const isEdit = !!location.state?.id;
    const editId = location.state?.id;

    const employeeCode = storageService.getItem('employeeCode') || storageService.getItem('employeeName') || '';

    useEffect(() => {
        const fetchMasterData = async () => {
            try {
                // Fetch Menus
                const menuRes = await apiServices({ method: 'GET', url: '/Residential/ResMenuList/getAllMenuList' });
                if (menuRes.success) {
                    const menuData = menuRes.data?.menuListResponse || [];
                    setMenus(menuData);
                }

                // If edit mode, fetch role details
                if (isEdit) {
                    setIsFetching(true);
                    const roleRes = await apiServices({ method: 'GET', url: `/Residential/ResRoleMaster/GetRoleById/${editId}` });

                    if (roleRes.success) {
                        let apiData = roleRes?.data?.data[0];

                        let menuIds = apiData.MenuListId || [];

                        if (Array.isArray(menuIds) && menuIds.length > 0 && typeof menuIds[0] === 'string') {
                            menuIds = menuIds[0].split(',').map(num => parseInt(num.trim(), 10)).filter(num => !isNaN(num));
                        } else if (typeof menuIds === 'string') {
                            menuIds = menuIds.split(',').map(num => parseInt(num.trim(), 10)).filter(num => !isNaN(num));
                        }

                        setFormData({
                            roleName: apiData.RoleName || '',
                            menuListId: menuIds,
                            isActive: apiData.IsActive || 'Active'
                        });
                    }
                }
            } catch (error) {
                toast.error("Failed to load necessary data.");
            } finally {
                setIsFetching(false);
            }
        };

        fetchMasterData();
    }, [isEdit, editId]);

    const handleInputChange = (e) => {
        const { id, value } = e.target;
        setFormData(prev => ({ ...prev, [id]: value }));
    };



    const handleSelectChange = (key, option) => {
        setFormData(prev => ({ ...prev, [key]: option ? option.value : null }));
    };

    const validateForm = () => {
        if (!formData.roleName || !formData.roleName.trim()) return toast.warning("Role Name is required"), false;
        if (!formData.menuListId || formData.menuListId.length === 0) return toast.warning("At least one Menu must be selected"), false;
        if (!formData.isActive) return toast.warning("Status is required"), false;
        return true;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;
        setIsLoading(true);

        const payload = {
            roleName: formData.roleName,
            MenuListId: formData.menuListId,
            isActive: formData.isActive === 'Active' ? 1 : 0,
            createdBy: employeeCode || 'sys'
        };

        try {
            const url = isEdit ? `/Residential/ResRoleMaster/UpdateExistingRole/${editId}` : `/Residential/ResRoleMaster/AddNewRole`;
            const method = isEdit ? 'PUT' : 'POST';
            const response = await apiServices({ method, url, data: payload });

            if (response.success) {
                toast.success(`Role ${isEdit ? 'updated' : 'created'} successfully!`);
                navigate('/zentres/site-management/role');
            } else {
                toast.error(response.message || `Failed to ${isEdit ? 'update' : 'create'} role.`);
            }
        } catch (error) {
            toast.error("An error occurred during submission.");
        } finally {
            setIsLoading(false);
        }
    };

    if (isFetching) {
        return (
            <div className="flex-1 flex justify-center items-center h-full">
                <FontAwesomeIcon icon={faSpinner} className="animate-spin text-4xl text-gray-300" />
            </div>
        );
    }

    return (
        <div className="bg-dashboardbg px-6 py-4 border border-bordercolor flex flex-col rounded-lg flex-1 overflow-y-auto pt-4">
            <Breadcrumbs />
            <PageHeadingSection
                Navbar={true}
                mainHeading={`${isEdit ? 'Update' : 'Add'} Role`}
                subHeading={`Please fill out the form below to ${isEdit ? 'update the existing' : 'create a new'} role.`}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                <div>
                    <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="roleName">
                        Role Name <span className="text-red-500">*</span>
                    </label>
                    <input
                        className="w-full px-3 py-2 border border-gray-200 dark:border-bordercolor rounded-md text-sm outline-none bg-transparent text-textprimary placeholder:text-gray-400 focus:border-gray-400 dark:focus:border-gray-500 transition-colors"
                        type="text"
                        id="roleName"
                        value={formData.roleName}
                        onChange={handleInputChange}
                        placeholder="e.g., Admin, User"
                    />
                </div>

                <div className="col-span-1 md:col-span-2 lg:col-span-1 z-50">
                    <MenuHierarchySelect
                        label={<>Select Menus <span className="text-red-500">*</span></>}
                        options={menus}
                        value={formData.menuListId}
                        onChange={(newIds) => setFormData(prev => ({ ...prev, menuListId: newIds }))}
                    />
                </div>

                <CustomSelect
                    label={<>Status <span className="text-red-500">*</span></>}
                    options={[{ label: 'Active', value: 'Active' }, { label: 'Inactive', value: 'Inactive' }]}
                    value={formData.isActive ? { label: formData.isActive, value: formData.isActive } : null}
                    onChange={(opt) => handleSelectChange('isActive', opt)}
                />
            </div>

            <div className="flex justify-between mt-auto border-t pt-4">
                <button
                    onClick={() => navigate("/zentres/site-management/role")}
                    className="px-3 py-2 border dark:border-bordercolor rounded-md text-xs flex items-center gap-2 text-gray-600 dark:text-textprimary hover:bg-gray-50 dark:hover:bg-actionitembg transition-colors"
                >
                    <FontAwesomeIcon icon={faAngleLeft} />
                    Cancel
                </button>

                <button
                    onClick={handleSubmit}
                    disabled={isLoading}
                    className="px-4 py-2 rounded-md text-xs font-bold flex items-center gap-2 shadow-sm hover:shadow-md active:scale-95 transition-all duration-200"
                    style={{ backgroundColor: secondaryColor(), color: primaryColor() }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = primaryColor();
                        e.currentTarget.style.color = '#FFFFFF';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = secondaryColor();
                        e.currentTarget.style.color = primaryColor();
                    }}
                >
                    {isLoading ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : (isEdit ? 'Update' : 'Submit')}
                    <FontAwesomeIcon icon={faAngleRight} />
                </button>
            </div>
        </div>
    );
};

export default RoleForm;
